import React from 'react';
import { IndianRupee, Pencil, Percent, Trash2 } from 'lucide-react';

const actionIconBtnClass =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#E2E8F0] bg-white text-[#64748B] transition-colors hover:bg-[#F8FAFC] hover:text-[#475569] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#CBD5E1] focus-visible:ring-offset-1';

const initialsThemes = [
  'bg-[#E7F2EF] border-[#BFE6DE] text-[#2A565E]',
  'bg-[#EEF2FF] border-[#D6DBFF] text-[#4F46E5]',
  'bg-[#FFF4E8] border-[#FBD7B2] text-[#B45309]',
  'bg-[#F3E8FF] border-[#E2CCFF] text-[#7E22CE]',
  'bg-[#EAF6FF] border-[#C5E4FA] text-[#0369A1]',
  'bg-[#F0FDF4] border-[#BBF7D0] text-[#166534]',
  'bg-[#FDF4FF] border-[#E9D5FF] text-[#86198F]'
];

const formatInr = (n) => new Intl.NumberFormat('en-IN').format(Number(n) || 0);

const ExtraChargeCard = ({ charge, onEdit, onDelete }) => {
  const initials = (charge?.name || 'EC')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

  const avatarSeed = String(charge?.id ?? charge?.name ?? 'EC')
    .split('')
    .reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  const initialsTheme = initialsThemes[avatarSeed % initialsThemes.length];

  const accNames = (charge?.accomodations || [])
    .map((a) => a?.accomodation?.name)
    .filter(Boolean);

  const isRequired = charge?.isMandatory === true || charge?.isMandatory === 'true';

  const priceNum = Number(charge?.price) || 0;
  const gstPct = charge?.gst != null && charge?.gst !== '' ? Number(charge.gst) : null;
  const gstAmount =
    gstPct != null && !Number.isNaN(gstPct) ? (priceNum * gstPct) / 100 : null;

  const modeLabel = charge?.mode ? String(charge.mode) : '—';

  return (
    <article className="group min-h-[200px] bg-white rounded-2xl border border-[#E2E8F0] p-4 flex flex-col gap-2.5 shadow-sm relative transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#D8DEE6] motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-sm">
      <div className="absolute top-3 right-3 z-10 flex items-center gap-1">
        <button
          type="button"
          aria-label="Edit extra charge"
          title="Edit"
          className={actionIconBtnClass}
          onClick={() => onEdit?.(charge.id)}>
          <Pencil size={17} strokeWidth={1.75} className="text-current" />
        </button>
        <button
          type="button"
          aria-label="Delete extra charge"
          title="Delete"
          className={actionIconBtnClass}
          onClick={() => onDelete?.(charge.id)}>
          <Trash2 size={17} strokeWidth={1.75} className="text-current" />
        </button>
      </div>

      <div className="flex-1 flex flex-col gap-2.5 pr-20">
        <div className="flex items-start gap-3">
          <div
            className={`h-14 w-14 rounded-xl border flex items-center justify-center text-lg font-semibold shrink-0 ${initialsTheme}`}>
            {initials || 'EC'}
          </div>
          <div className="min-w-0">
            <p
              className="text-[18px] leading-tight font-semibold text-[#1A202C] truncate"
              title={charge?.name || ''}>
              {charge?.name || '---'}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-semibold bg-[#F3E8FF] text-[#6B46C1]">
                {modeLabel}
              </span>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-semibold ${
                  isRequired
                    ? 'bg-[#FEE2E2] text-[#B91C1C]'
                    : 'bg-[#F1F5F9] text-[#64748B]'
                }`}>
                {isRequired ? 'Required' : 'Optional'}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {accNames.length > 0 ? (
            accNames.map((name, idx) => (
              <span
                key={`${charge.id}-acc-${idx}`}
                className="inline-flex max-w-full items-center rounded-full border border-[#E2E8F0] bg-[#F8FAFC] px-2.5 py-1 text-[12px] font-medium text-[#475569] truncate"
                title={name}>
                {name}
              </span>
            ))
          ) : (
            <span className="inline-flex rounded-full border border-dashed border-[#CBD5E1] bg-white px-2.5 py-1 text-[12px] text-[#94A3B8]">
              No accommodations
            </span>
          )}
        </div>

        {charge?.description ? (
          <p className="text-xs text-[#94A3B8] line-clamp-2" title={charge.description}>
            {charge.description}
          </p>
        ) : null}

        <div className="flex flex-wrap gap-2 mt-auto pt-1">
          <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[13px] font-semibold bg-[#E6FFFA] text-[#2C7A7B]">
            <IndianRupee size={12} strokeWidth={2.5} />
            ₹{formatInr(priceNum)}
          </span>
          {gstPct != null && !Number.isNaN(gstPct) && (
            <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[13px] font-semibold bg-[#EBF4FF] text-[#5A67D8]">
              <Percent size={12} strokeWidth={2.5} />
              GST: {gstPct.toFixed(2)}%
              {gstAmount != null && (
                <span className="font-medium opacity-90"> (₹{formatInr(gstAmount)})</span>
              )}
            </span>
          )}
        </div>
      </div>
    </article>
  );
};

export default ExtraChargeCard;
